// The MEMBER's own payment-failing notice on the home dashboard (df-6f0a2f8274a3),
// as one unit: its state, when it shows, what it says, and what happens when it is
// dismissed or tapped.
//
// Moved out of CsDayItemsComponent by #5236, unchanged. The rule about WHETHER to
// show it does not live here — that is `decideMemberBillingCard` in
// cs-day-items-member-billing-card.ts, which is pure and separately tested, and
// which carries the "coach trap" this notice must never re-derive. This file
// gathers that rule's inputs, formats its answer for the template, and owns the
// dismissal and the action.
//
// Angular-free on purpose — see cs-day-items-notice-host.ts for why.

import { DateTime } from 'luxon';
import { formatDateMed } from '@cf-shared/date-format';
import { resolveSubscriptionEndInstants } from '@cf-shared/data-model/subscription-end-instants';
import {
  MemberBillingBlocks,
  decideMemberBillingCard,
  isMemberBillingCardDismissed,
  markMemberBillingCardDismissed,
} from './cs-day-items-member-billing-card';
import { DayItemsNoticeController, DayItemsNoticeHost, markNoticeHostForCheck } from './cs-day-items-notice-host';

export interface MemberBillingNoticeDeps {
  host: DayItemsNoticeHost;
  /**
   * True while a coach is managing one of their clients. The screen then shows the
   * CLIENT's data, so the coach's own payment warning must not render over it.
   */
  isCoachManaging(): boolean;
  /**
   * The signed-in person's own login result. `CouchLoginResult` satisfies
   * `MemberBillingBlocks` structurally, so the component passes it straight in.
   */
  getMember(): MemberBillingBlocks | null | undefined;
  /** The signed-in person's uid, which keys the per-session dismissal. */
  getUid(): string | undefined;
  /** Evaluation time, supplied so this stays testable without faking the clock. */
  now(): DateTime;
  /** The locale the date is formatted in. */
  locale: string;
  /** Sends the person to their own membership page. */
  openMembershipPage(): void;
}

/**
 * The member's own payment-failing card.
 *
 * Cheap by construction: it reads the cached login result and nothing else — no
 * server call and no DocWatcher — so it costs essentially nothing on the home
 * screen of the very large majority of people it does not apply to. That is why
 * it has nothing to dispose.
 */
export class CsMemberBillingNotice implements DayItemsNoticeController {
  /** Whether this notice has something to say. Read by `decideVisibleNotices`. */
  wantsToShow = false;
  /**
   * The date the membership runs to if the payment keeps failing, formatted for
   * display. Empty when there is none worth quoting, and the template then draws
   * the wording that names no date.
   */
  endDate = '';

  constructor(private deps: MemberBillingNoticeDeps) {}

  /**
   * Re-decides whether the card shows, and with which date.
   *
   * The licence half changes only on a fresh sign-in, which re-creates the host
   * component, so one call at view init is all this notice needs — there is no
   * roster or other late-arriving document to watch for.
   */
  refresh(): void {
    // while a coach is managing a client the screen shows the CLIENT's data, but
    // the login result still describes the coach — so without this the coach's own
    // payment warning would render on the client's dashboard. Reachable for an
    // admin-granted Legacy coach, who has no coaching rail (so the coach card stays
    // silent) and whose personal membership can be an ordinary Paddle
    // subscription. The action would mislead too: in managing mode the profile page
    // it pushes shows the CLIENT's membership, not the coach's. Same guard, same
    // reason, as the coach billing notice.
    if (this.deps.isCoachManaging()) {
      this.hide();
      return;
    }

    const decision = decideMemberBillingCard({
      member: this.deps.getMember(),
      now: this.deps.now(),
      dismissedThisSession: isMemberBillingCardDismissed(this.deps.getUid()),
    });

    if (!decision.show) {
      this.hide();
      return;
    }

    // The date the person acts on must be their OWN day: the stored expiry is UTC,
    // so somebody at UTC-7 whose membership ends 02:00Z would otherwise be told the
    // following date. Names and order then follow the UI language — see
    // design-docs/date-time-localization.md. Same treatment as the coach card's
    // date, deliberately.
    // "your membership will end on <date>" asks when OUR ACCESS ends, so this is
    // the access answer — the stored value as written, with no grace taken off.
    // Routed through the shared helper rather than parsed here so this site
    // cannot drift from the rest (#4278); the rail is left unknown because the
    // access answer does not depend on it.
    const { accessEndsAt } = resolveSubscriptionEndInstants(undefined, decision.endDate, {});
    this.endDate = accessEndsAt ? formatDateMed(accessEndsAt.toLocal(), this.deps.locale) : '';
    this.wantsToShow = true;
    markNoticeHostForCheck(this.deps.host);
  }

  /**
   * Dismissal is per SESSION only — see `dismissedByUid` in the card module for
   * why. It must not suppress the warning for the rest of the retry window, which
   * is the whole problem this card was added to fix.
   */
  onDismissed(): void {
    markMemberBillingCardDismissed(this.deps.getUid());
    this.hide();
  }

  /**
   * Sends the person to their own membership page, which already carries the
   * fuller note and the Cancel / Manage controls with their correct per-platform
   * gating.
   *
   * Deliberately an IN-APP navigation and not a link to Paddle's portal. Updating
   * a card is a web action, and pointing at an outside payment flow from inside a
   * store app is the anti-steering line #4247 draws — see
   * design-docs/paddle-integration.md. The profile page is where that decision
   * already lives, so this card does not have to make it a second time.
   */
  onActionClick(): void {
    this.deps.openMembershipPage();
  }

  /** Nothing to release: this notice creates no watcher and no listener. */
  dispose(): void {}

  private hide(): void {
    this.wantsToShow = false;
    markNoticeHostForCheck(this.deps.host);
  }
}
